import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export type ApplicationListItem = Record<string, unknown>;
export type ApplicationDetail = Record<string, unknown>;

export interface ApplicationQuestionMapping {
  id: number | null;
  question_name: string;
  integration_question_id: string;
  question_id: string;
}

export interface ApplicationDetailResponse {
  output: ApplicationDetail;
  questionMappings: ApplicationQuestionMapping[];
}

export interface ApplicationCreateRequestBody {
  isActive: boolean;
  applicationName: string;
  contactName: string;
  contactEmail: string;
  webhookUrl: string;
  webhookAuthenticationKey: string;
  webhookAuthenticationType: string;
}

export interface ApplicationUpdateRequestBody {
  id: number;
  applicationName: string;
  contactName: string;
  contactEmail: string;
  webhookUrl: string;
  webhookAuthenticationKey: string;
  webhookAuthenticationType: string;
}

export interface ApplicationQuestionAddRequestBody {
  ApplicationId: number;
  IntegrationQuestionId: string;
  QuestionId: number;
  CategoriesSendResponse: number;
}

export interface ApplicationQuestionUpdateRequestBody {
  Id: number;
  IntegrationQuestionId: string;
  QuestionId: number;
  CategoriesSendResponse: number;
}

@Injectable({
  providedIn: 'root'
})
export class ApplicationsService {
  private readonly httpOptions = {
    headers: new HttpHeaders()
      .set('Authorization', environment.questionsApiToken)
      .set('Accept', '*')
      .set('Access-Control-Allow-Origin', '*')
      .set('Access-Control-Allow-Headers', 'Origin, X-Requested-With, Content-Type, Accept')
  };

  private readonly applicationsUrl = `${environment.apiRoot}application`;
  private readonly applicationsSearchUrl = `${this.applicationsUrl}/search`;
  private readonly applicationStatusUrl = `${this.applicationsUrl}/status`;
  private readonly applicationQuestionsUrl = `${environment.apiRoot}ApplicationQuestions/`;

  constructor(private http: HttpClient) { }

  list(): Observable<ApplicationListItem[]> {
    return this.http.get<ApplicationListItem[]>(this.applicationsUrl, this.httpOptions);
  }

  search(terms: string): Observable<ApplicationListItem[]> {
    return this.http.post<ApplicationListItem[]>(this.applicationsSearchUrl, { terms }, this.httpOptions);
  }

  getById(id: string | number): Observable<ApplicationDetailResponse> {
    return this.http.get<ApplicationDetailResponse>(`${this.applicationsUrl}/${id}`, this.httpOptions);
  }

  create(body: ApplicationCreateRequestBody): Observable<number> {
    return this.http.post<number>(this.applicationsUrl, body, this.httpOptions);
  }

  update(body: ApplicationUpdateRequestBody): Observable<number> {
    return this.http.put<number>(this.applicationsUrl, body, this.httpOptions);
  }

  setStatus(id: number, isActive: boolean): Observable<void> {
    return this.http.put<void>(this.applicationStatusUrl, { id, isActive }, this.httpOptions);
  }

  addQuestionMapping(body: ApplicationQuestionAddRequestBody): Observable<number> {
    return this.http.post<number>(this.applicationQuestionsUrl, body, this.httpOptions);
  }

  updateQuestionMapping(body: ApplicationQuestionUpdateRequestBody): Observable<number> {
    return this.http.put<number>(this.applicationQuestionsUrl, body, this.httpOptions);
  }

  deleteQuestionMapping(id: number): Observable<void> {
    return this.http.delete<void>(`${this.applicationQuestionsUrl}?id=${id}`, this.httpOptions);
  }
}
